import type { SourceRef } from '@semeris/workspace-ui/chat-config';

import { PanelViewId } from '../../../store/panel/panel.state';
import { SemerisRoutes } from '../../shared/constants/routes';

/**
 * Kebab-cased `ref-*` keys the semeris coordinates arrive under — ai-server sends some, and a
 * clicked ref badge contributes them via `SourceRef.dataAttrs`, which the chat package passes
 * through without naming them. `documentShortName` / `transactionId` are also named fields on
 * `SourceRef`; the manager is ours alone, so it only ever comes through the bag.
 */
export const REF_TRANSACTION_ID = 'ref-transaction-id';
export const REF_DOCUMENT_SHORT_NAME = 'ref-document-short-name';
export const REF_MANAGER_SHORT_NAME = 'ref-manager-short-name';

/**
 * Absolute deal-details URL for a citation, so a copied reference pastes as a working link.
 *
 * `null` whenever the coordinates are incomplete — the route needs all three of
 * `:libraryShortName/:managerName/:dealShortName`, and only documents ingested with a
 * `managerShortName` stamp carry the manager.
 */
export function buildDealRefHref(ref: SourceRef): string | null {
  const transactionId = ref.transactionId ?? ref.dataAttrs?.[REF_TRANSACTION_ID];
  const documentShortName = ref.documentShortName ?? ref.dataAttrs?.[REF_DOCUMENT_SHORT_NAME];
  const managerShortName = ref.dataAttrs?.[REF_MANAGER_SHORT_NAME];
  if (!transactionId || !documentShortName || !managerShortName) {
    return null;
  }

  const dot = transactionId.indexOf('.');
  if (dot <= 0) {
    return null;
  }
  const libraryShortName = transactionId.slice(0, dot);
  const dealShortName = transactionId.slice(dot + 1);
  if (!dealShortName) {
    return null;
  }

  const params = new URLSearchParams({ panels: PanelViewId.Document, documentShortName });
  const path = [SemerisRoutes.transactions.root, libraryShortName, managerShortName, dealShortName]
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${location.origin}/${path}?${params.toString()}`;
}
